import React, { useState } from "react";
import { ImageDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { previewLibraryMigration, runLibraryMigration } from "@/lib/api";

const FIELD = { "shop.image": "Shop images", "shop.logo": "Shop logos", "product.image": "Product cover images", "product.images": "Product gallery images" };

function Report({ r }) {
  const c = r.counts;
  return (
    <div className="mt-3 text-xs" data-testid={`library-migration-${r.dry_run ? "preview" : "result"}`}>
      <div className="font-semibold text-slate-700 mb-1">{r.dry_run ? "Preview (nothing changed yet)" : "Migration result"}</div>
      <ul className="grid grid-cols-2 gap-x-3 gap-y-0.5">
        {Object.entries(r.by_field).map(([k, v]) => <li key={k} data-testid={`library-migration-field-${k}`}>{FIELD[k] || k}: <b>{v.eligible}</b> eligible{!r.dry_run && ` · ${v.migrated} migrated · ${v.failed} failed`}</li>)}
      </ul>
      <div className="mt-1.5 text-slate-600" data-testid="library-migration-counts">
        Eligible <b>{c.eligible}</b>{!r.dry_run && <> · Migrated <b>{c.migrated}</b> · Failed <b>{c.failed}</b></>} · Already in VerB storage (skipped) <b>{c.already_migrated}</b> · Unchanged records <b>{c.unchanged_records}</b>
      </div>
      {r.failures.length > 0 && (
        <ul className="mt-1.5 text-rose-700 space-y-0.5" data-testid="library-migration-failures">
          {r.failures.map((f, i) => <li key={i}>{f.record_type} {f.record_id} · {f.field}: {f.error} (old link kept)</li>)}
        </ul>
      )}
    </div>
  );
}

export default function AdminLibraryMigration() {
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const check = async () => {
    setBusy("preview"); setResult(null);
    try { setPreview(await previewLibraryMigration()); } catch { toast.error("Could not check library photos"); } finally { setBusy(""); }
  };
  const run = async () => {
    if (!window.confirm(`Copy ${preview.counts.eligible} library photo link(s) into VerB storage and update those records?`)) return;
    setBusy("run");
    try {
      const r = await runLibraryMigration();
      setResult(r); setPreview(null);
      toast.success(`Migrated ${r.counts.migrated}, failed ${r.counts.failed}`);
    } catch { toast.error("Migration could not run"); } finally { setBusy(""); }
  };
  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-4 sm:p-5" data-testid="library-migration-card">
      <div className="font-display font-bold flex items-center gap-2"><ImageDown className="w-4 h-4 text-emerald-700" /> Library photos</div>
      <p className="text-xs text-slate-500 mt-0.5">Copy older VerB library (Unsplash) links on shops and products into VerB storage. A preview runs first; nothing changes until you confirm.</p>
      <div className="mt-3 flex gap-2 flex-wrap">
        <button type="button" onClick={check} disabled={!!busy} className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white px-4 py-2 text-xs font-semibold disabled:opacity-60" data-testid="library-migration-check">
          {busy === "preview" && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Library photos ko VerB storage me copy karein
        </button>
        {preview && preview.counts.eligible > 0 && (
          <button type="button" onClick={run} disabled={!!busy} className="inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 text-amber-900 px-4 py-2 text-xs font-semibold disabled:opacity-60" data-testid="library-migration-run">
            {busy === "run" && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Confirm & copy {preview.counts.eligible}
          </button>
        )}
      </div>
      {preview && <Report r={preview} />}
      {preview && preview.counts.eligible === 0 && <div className="mt-2 text-xs text-emerald-700" data-testid="library-migration-none">No old library links found — nothing to migrate.</div>}
      {result && <Report r={result} />}
    </div>
  );
}
